import { apiClient } from '@features/shared/api/client'
import type {
  AssignedPermissionBatchRequest,
  AssignedPermissionDetail,
  CreateUserRequest,
  ImplicationRule,
  Module,
  PermissionAction,
  ResolvedPermissionDetail,
  ResourcePermissionEditorResponse,
  ResourceType,
  UpdateUsersRequest,
  UserDetail,
  UserGroupDetail,
} from './contracts'
import type { CreateUserGroupRequest, UpdateUserGroupRequest, UpdateUserRequest } from '../models'
import type { ListTablesResponse, TableDetail } from '../../tables/api/contracts'
import type { Table, TableSchema } from '../../tables/models'

// The list endpoints stop at 100 rows unless asked for more. A screen that
// shows or checks against a whole list reads every page, so the 101st user,
// group or grant is never silently missing.
const ALL_PAGES_PAGE_SIZE = 1000
// A group's grants run to one per table and field (about ten thousand for an
// administrator group), so they're read up to the endpoint's larger cap.
const ALL_ASSIGNED_PAGE_SIZE = 10000

async function getAllPages<T>(
  getPage: (page: { limit: number; offset: number }) => Promise<T[]>,
  pageSize = ALL_PAGES_PAGE_SIZE
): Promise<T[]> {
  const all: T[] = []
  for (let offset = 0; ; offset += pageSize) {
    const page = await getPage({ limit: pageSize, offset })
    all.push(...page)
    if (page.length < pageSize) return all
  }
}

// ======================================================
// Users
// ======================================================

export async function getUsers(
  params: { limit?: number; offset?: number } = {}
): Promise<UserDetail[]> {
  const { data } = await apiClient.get<UserDetail[]>('/permissions/users', { params })
  return data
}

/** Every user, past the endpoint's 100-row default. */
export function getAllUsers(): Promise<UserDetail[]> {
  return getAllPages(getUsers)
}

export async function getUser(userId: string): Promise<UserDetail> {
  const { data } = await apiClient.get<UserDetail>(`/permissions/users/${userId}/meta`)
  return data
}

export async function createUser(payload: CreateUserRequest): Promise<UserDetail> {
  const { data } = await apiClient.post<UserDetail>('/permissions/users', payload)
  return data
}

export async function updateUser(userId: string, payload: UpdateUserRequest): Promise<UserDetail> {
  const { data } = await apiClient.patch<UserDetail[]>('/permissions/users', [
    { user_id: userId, ...payload } satisfies UpdateUsersRequest,
  ])
  const updatedUser = data[0]
  if (!updatedUser) throw new Error('User update did not return an updated user')
  return updatedUser
}

export async function sendUserInvitation(userId: string): Promise<UserDetail> {
  const { data } = await apiClient.post<UserDetail>(`/permissions/users/${userId}/invite`)
  return data
}

/**
 * Backend accepts a list (single deletion = list of length 1) and returns
 * the deleted users' detail records.
 */
export async function deleteUsers(userIds: string[]): Promise<UserDetail[]> {
  const { data } = await apiClient.delete<UserDetail[]>('/permissions/users', { data: userIds })
  return data
}

export async function applyPermissionsToUser(
  userId: string,
  payload: AssignedPermissionBatchRequest
): Promise<AssignedPermissionDetail[]> {
  const { data } = await apiClient.post<AssignedPermissionDetail[]>(
    `/permissions/users/${userId}`,
    payload
  )
  return data
}

// ======================================================
// User Groups
// ======================================================

export async function getUserGroups(
  params: { limit?: number; offset?: number } = {}
): Promise<UserGroupDetail[]> {
  const { data } = await apiClient.get<UserGroupDetail[]>('/permissions/user-groups', { params })
  return data
}

/** Every permission group, past the endpoint's 100-row default. */
export function getAllUserGroups(): Promise<UserGroupDetail[]> {
  return getAllPages(getUserGroups)
}

export async function getUserGroup(userGroupId: string): Promise<UserGroupDetail> {
  const { data } = await apiClient.get<UserGroupDetail>(
    `/permissions/user-groups/${userGroupId}/meta`
  )
  return data
}

export async function createUserGroup(payload: CreateUserGroupRequest): Promise<UserGroupDetail> {
  const { data } = await apiClient.post<UserGroupDetail>('/permissions/user-groups', payload)
  return data
}

export async function updateUserGroup(
  userGroupId: string,
  payload: UpdateUserGroupRequest
): Promise<UserGroupDetail> {
  const { data } = await apiClient.patch<UserGroupDetail>(
    `/permissions/user-groups/${userGroupId}`,
    payload
  )
  return data
}

/**
 * Returns `{ ok: true }` per backend contract — we discard it; the caller
 * should invalidate the user-groups query to pick up the removal.
 */
export async function deleteUserGroups(userGroupIds: string[]): Promise<void> {
  await apiClient.delete('/permissions/user-groups', { data: userGroupIds })
}

export async function addUsersToGroup(
  userGroupId: string,
  userIds: string[]
): Promise<UserGroupDetail> {
  const { data } = await apiClient.post<UserGroupDetail>(
    `/permissions/user-groups/${userGroupId}/members`,
    userIds
  )
  return data
}

export async function removeUsersFromGroup(
  userGroupId: string,
  userIds: string[]
): Promise<UserGroupDetail> {
  const { data } = await apiClient.delete<UserGroupDetail>(
    `/permissions/user-groups/${userGroupId}/members`,
    { data: userIds }
  )
  return data
}

export async function applyPermissionsToGroup(
  userGroupId: string,
  payload: AssignedPermissionBatchRequest
): Promise<AssignedPermissionDetail[]> {
  const { data } = await apiClient.post<AssignedPermissionDetail[]>(
    `/permissions/user-groups/${userGroupId}`,
    payload
  )
  return data
}

// ======================================================
// Assigned Permissions (editable source of truth)
// ======================================================

export interface ListAssignedPermissionsParams {
  /**
   * Backend query param is `prm_user_id`, not `user_id` — the doc uses
   * the shorter name but the FastAPI signature is namespaced. Keep this
   * mapping centralized here.
   */
  prm_user_id?: string
  user_group_id?: string
  target_resource_id?: string
  target_resource_type_id?: string
  result_resource_type_id?: string
  limit?: number
  offset?: number
}

export async function getAssignedPermissions(
  params: ListAssignedPermissionsParams = {}
): Promise<AssignedPermissionDetail[]> {
  const { data } = await apiClient.get<AssignedPermissionDetail[]>('/permissions/assigned', {
    params,
  })
  return data
}

/** Every filtered assignment, past the endpoint's 100-row default: grids must not silently truncate. */
export function getAllAssignedPermissions(
  params: Omit<ListAssignedPermissionsParams, 'limit' | 'offset'> = {}
): Promise<AssignedPermissionDetail[]> {
  return getAllPages(
    (page) => getAssignedPermissions({ ...params, ...page }),
    ALL_ASSIGNED_PAGE_SIZE
  )
}

export async function getResourcePermissionEditor(
  resourceTypeName: string,
  resourceId: string
): Promise<ResourcePermissionEditorResponse> {
  const { data } = await apiClient.get<ResourcePermissionEditorResponse>(
    `/permissions/resources/${resourceTypeName}/${resourceId}/editor`
  )
  return data
}

// ======================================================
// Resolved Permissions (read-only, derived)
// ======================================================

export interface ListResolvedPermissionsParams {
  prm_user_id?: string
  target_resource_id?: string
  target_resource_type_id?: string
  result_resource_type_id?: string
  limit?: number
  offset?: number
}

export async function getResolvedPermissions(
  params: ListResolvedPermissionsParams = {}
): Promise<ResolvedPermissionDetail[]> {
  const { data } = await apiClient.get<ResolvedPermissionDetail[]>('/permissions/resolved', {
    params,
  })
  return data
}

// ======================================================
// Permission Catalog
// ======================================================

export async function getPermissionTables(): Promise<Table[]> {
  const { data } = await apiClient.get<ListTablesResponse>('/permissions/tables')
  return data.tables.map(
    (table) =>
      ({
        ...table,
        default_label_column: null,
      }) as unknown as Table
  )
}

type PendingPermissionTableSchemaRequest = {
  resolve: (schema: TableSchema) => void
  reject: (error: unknown) => void
}

const pendingPermissionTableSchemaRequests = new Map<
  string,
  PendingPermissionTableSchemaRequest[]
>()
let permissionTableSchemaBatchTimer: ReturnType<typeof setTimeout> | null = null

async function flushPermissionTableSchemaBatch() {
  permissionTableSchemaBatchTimer = null

  const pending = new Map(pendingPermissionTableSchemaRequests)
  pendingPermissionTableSchemaRequests.clear()
  const tableIds = [...pending.keys()]

  try {
    const { data } = await apiClient.post<TableDetail[]>('/permissions/tables/meta', tableIds)
    const schemasByTableId = new Map(
      data.map((schema) => [
        schema.id,
        {
          ...schema,
          table_id: schema.id,
          display_name: schema.display_name_plural,
          default_label_column: schema.default_label_column_id,
          columns: schema.columns,
        } as unknown as TableSchema,
      ])
    )

    for (const tableId of tableIds) {
      const schema = schemasByTableId.get(tableId)

      if (!schema) {
        const error = new Error(`Permission table metadata response missing table ${tableId}`)
        pending.get(tableId)?.forEach(({ reject }) => reject(error))
        continue
      }

      pending.get(tableId)?.forEach(({ resolve }) => resolve(schema))
    }
  } catch (error) {
    for (const requests of pending.values()) {
      requests.forEach(({ reject }) => reject(error))
    }
  }
}

export function getPermissionTableSchema(tableId: string): Promise<TableSchema> {
  return new Promise((resolve, reject) => {
    const pending = pendingPermissionTableSchemaRequests.get(tableId)

    if (pending) {
      pending.push({ resolve, reject })
    } else {
      pendingPermissionTableSchemaRequests.set(tableId, [{ resolve, reject }])
    }

    if (permissionTableSchemaBatchTimer === null) {
      permissionTableSchemaBatchTimer = setTimeout(() => void flushPermissionTableSchemaBatch(), 0)
    }
  })
}

// ======================================================
// Metadata
// ======================================================

export async function getPermissionModules(): Promise<Module[]> {
  const { data } = await apiClient.get<Module[]>('/permissions/modules/catalog')
  return data
}

export async function getModules(): Promise<Module[]> {
  const { data } = await apiClient.get<Module[]>('/permissions/modules')
  return data
}

export async function getPermissionActions(): Promise<PermissionAction[]> {
  const { data } = await apiClient.get<PermissionAction[]>('/permissions/actions')
  return data
}

export async function getPermissionImplicationRules(): Promise<ImplicationRule[]> {
  const { data } = await apiClient.get<ImplicationRule[]>('/permissions/implication-rules')
  return data
}

export async function getResourceTypes(): Promise<ResourceType[]> {
  const { data } = await apiClient.get<ResourceType[]>('/permissions/resource-types')
  return data
}
